/**
 * XOT shared formatters — pure helpers, no DOM, no globals.
 *
 * Currently exports:
 *   - fmtTime(seconds): "M:SS" for under one hour, "H:MM:SS" beyond.
 *
 * Replaces two divergent inline copies that existed before Phase 2:
 *   - assets/js/single-article.js — minutes-only, Math.round
 *   - assets/js/xot-podcast-discovery.js — hour-aware, Math.floor
 *
 * Unified behavior matches industry standard (Spotify / Apple Music):
 *   - Math.floor (never round up — a 44.9s position never reads "0:45")
 *   - Hour segment shown only when needed (compact for short content)
 *   - Negative / non-finite / NaN inputs all collapse to "0:00"
 *
 * Build path:
 *   src                      assets/js/src/lib/xot-fmt.ts
 *   ↓ esbuild (IIFE, target ES2020)
 *   dist                     assets/dist/js/lib/xot-fmt.js   (attaches window.XOT.fmt)
 *
 * Test path:
 *   src                      assets/js/src/lib/xot-fmt.ts
 *   ↓ vitest (transforms TS, jsdom env)
 *   test                     tests/js/xot-fmt.test.ts        (imports named exports)
 *
 * @since Phase 2 — JS modernization, bite 1
 */

/**
 * Format a duration in seconds as `M:SS` or `H:MM:SS`.
 *
 * @param seconds  Non-negative duration. Non-finite / negative inputs return "0:00".
 * @returns        Compact display string, no leading hour when sub-3600s.
 *
 * @example fmtTime(0)      // "0:00"
 * @example fmtTime(45)     // "0:45"
 * @example fmtTime(65)     // "1:05"
 * @example fmtTime(3661)   // "1:01:01"
 */
export function fmtTime(seconds: number): string {
    if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
    const total = Math.floor(seconds);
    const h     = Math.floor(total / 3600);
    const m     = Math.floor((total % 3600) / 60);
    const s     = total % 60;
    const pad   = (n: number) => (n < 10 ? '0' + n : String(n));
    return h > 0
        ? `${h}:${pad(m)}:${pad(s)}`
        : `${m}:${pad(s)}`;
}

/* ── window.XOT.fmt attachment (legacy IIFE consumers) ─────────────────
   Mirrors xot-storage.ts pattern: side-effect on import so the bundled
   IIFE dist makes the helpers available globally without requiring every
   consumer to be migrated to ESM in the same change.
*/
declare global {
    interface Window {
        XOT?: {
            fmt?: { time: typeof fmtTime };
            [k: string]: unknown;
        };
    }
}

if (typeof window !== 'undefined') {
    window.XOT = window.XOT ?? {};
    window.XOT.fmt = { time: fmtTime };
}
